import type { ReviewStatus } from './types';

export const MAX_REVIEW_TARGET = 999;

export const REVIEW_STATUS_FILTERS = [
  'interested',
  'not_interested',
  'uncertain',
  'conflict',
  'unreviewed',
] as const;

export type ReviewStatusFilter = (typeof REVIEW_STATUS_FILTERS)[number];

export interface ReviewStatusCounts {
  interested: number;
  not_interested: number;
  uncertain: number;
  conflict: number;
  unreviewed: number;
  total: number;
}

const STATUS_LABELS: Record<ReviewStatusFilter, string> = {
  interested: 'Interested',
  not_interested: 'Not interested',
  uncertain: 'Would review if needed',
  conflict: 'Conflict',
  unreviewed: 'Unreviewed',
};

export function reviewStatusLabel(status: ReviewStatusFilter): string {
  return STATUS_LABELS[status];
}

export function countReviewStatuses(
  papers: readonly { id: string }[],
  decisions: Readonly<Record<string, { status?: ReviewStatus }>>,
): ReviewStatusCounts {
  const counts: ReviewStatusCounts = {
    interested: 0,
    not_interested: 0,
    uncertain: 0,
    conflict: 0,
    unreviewed: 0,
    total: papers.length,
  };
  for (const paper of papers) {
    const status = decisions[paper.id]?.status;
    if (
      status === 'interested' ||
      status === 'not_interested' ||
      status === 'uncertain' ||
      status === 'conflict'
    ) {
      counts[status] += 1;
    } else {
      counts.unreviewed += 1;
    }
  }
  return counts;
}

export function normalizeReviewTarget(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) return null;
  return Math.min(value, MAX_REVIEW_TARGET);
}

export function reviewProgressMessage(
  counts: ReviewStatusCounts,
  target: number | null,
): string {
  if (counts.total === 0) {
    return target === null
      ? 'No papers imported yet. Set an interested target if your chair asked for a quota.'
      : `Need ${target} interested paper${target === 1 ? '' : 's'} once papers are imported.`;
  }
  if (target === null) {
    return `${counts.interested} interested · ${counts.unreviewed} unreviewed of ${counts.total}.`;
  }
  if (counts.interested >= target) {
    return `${counts.interested} of ${target} interested · target met.`;
  }
  const remaining = target - counts.interested;
  return `${counts.interested} of ${target} interested · ${remaining} more needed.`;
}
